/**
 * ECIES payload encryption (P-256 + AES-256-GCM) using browser crypto.subtle.
 *
 * Versioned key registry: the SDK carries one or more public keys, each
 * tagged with a `kid`. The wire format includes `kid` so the server can
 * route to the matching private key. This enables rotation without forcing
 * every customer to rebuild on key roll: ship a new SDK version that
 * adds the new key alongside the old one, set `validFrom` in the future,
 * cut over server-side, then drop the old key from the SDK in a later
 * release once all clients have updated.
 *
 * Selection: the SDK picks the newest key whose `validFrom` is <= now. If
 * no key qualifies (clock skew, misconfiguration), it falls back to the
 * registry's first entry. The server accepts any `kid` in its registry.
 *
 * To rotate keys:
 *   1. node scripts/generate-keypair.mjs → produces { kid, public_jwk, private_jwk }
 *   2. SDK: prepend the new entry to SERVER_PUBLIC_KEYS with a future validFrom
 *   3. Server: add the new private key to FP_PRIVATE_KEYS (JSON array of
 *      { kid, jwk } objects) — keep the previous key online during overlap
 *   4. Publish SDK; wait for adoption; then once you're confident customers
 *      have updated, drop the old key from both sides.
 */

interface KeyEntry {
  /** Stable identifier embedded in the wire payload. ~8 chars is plenty. */
  kid: string;
  /** P-256 public key as a JWK. The matching private key is on the server. */
  jwk: JsonWebKey;
  /**
   * UNIX seconds. Keys are eligible for use from this timestamp onward.
   * Use to schedule a rotation: ship the new key with validFrom in the
   * future so a release goes out before the cutover takes effect.
   * Defaults to 0 (immediately valid) when omitted.
   */
  validFrom?: number;
}

/**
 * Server public-key registry, ordered newest-first. The first entry is the
 * preferred encryption key once it becomes valid. Add new keys to the top
 * during rotation; remove retired keys only after server side has dropped them.
 */
const SERVER_PUBLIC_KEYS: KeyEntry[] = [
  {
    kid: "k1",
    jwk: {
      kty: "EC",
      crv: "P-256",
      x: "rg3rrAKcFVZDiNEk2_9k_t3YVUixmw-GhE-HmhsUoYk",
      y: "3a7czZDUWDwSLCzni81fikJ1GDR8HtGQeMLtFyBi0mQ",
    },
  },
];

const _importedKeys = new Map<string, CryptoKey>();

function pickActiveKey(): KeyEntry {
  const nowSec = Math.floor(Date.now() / 1000);
  // Newest-first: return the first entry whose validFrom is <= now.
  for (const entry of SERVER_PUBLIC_KEYS) {
    if ((entry.validFrom ?? 0) <= nowSec) return entry;
  }
  // Clock skew or misconfiguration — fall back to the first registry entry.
  // Never throw at runtime; encryption must always succeed in a secure
  // context, even if it means using a key with a future validFrom.
  return SERVER_PUBLIC_KEYS[0]!;
}

async function getServerPublicKey(entry: KeyEntry): Promise<CryptoKey> {
  const cached = _importedKeys.get(entry.kid);
  if (cached) return cached;
  const imported = await crypto.subtle.importKey(
    "jwk",
    entry.jwk,
    { name: "ECDH", namedCurve: "P-256" },
    false,
    [],
  );
  _importedKeys.set(entry.kid, imported);
  return imported;
}

function bufToBase64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  return btoa(String.fromCharCode(...bytes));
}

/**
 * Wire payload for /v1/collect: ECIES (v=1) with a key-id, or plaintext (v=0)
 * fallback when Web Crypto is unavailable.
 *
 * The `kid` field is required on v=1 payloads. The legacy v=1 format without
 * `kid` is still accepted server-side for one release as a transition
 * compatibility window — see server/src/crypto.ts.
 */
export type CollectPayloadWire =
  | { v: 1; kid: string; epk: string; iv: string; ct: string }
  | { v: 0; plaintext: string };

/**
 * Encrypt a plaintext string with ECIES (ephemeral ECDH + AES-256-GCM).
 *
 * On non-secure contexts (e.g. HTTP on a custom dev hostname), `crypto.subtle`
 * is missing — returns `{ v: 0, plaintext }` so a **test** API key can still collect.
 */
export async function encryptPayload(plaintext: string): Promise<CollectPayloadWire> {
  if (typeof crypto === "undefined" || !crypto.subtle) {
    return { v: 0, plaintext };
  }

  const keyEntry = pickActiveKey();
  const serverPubKey = await getServerPublicKey(keyEntry);

  const ephemeral = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, true, [
    "deriveKey",
  ]);

  const aesKey = await crypto.subtle.deriveKey(
    { name: "ECDH", public: serverPubKey },
    ephemeral.privateKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"],
  );

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    aesKey,
    new TextEncoder().encode(plaintext),
  );

  const epkRaw = await crypto.subtle.exportKey("raw", ephemeral.publicKey);

  return {
    v: 1,
    kid: keyEntry.kid,
    epk: bufToBase64(epkRaw),
    iv: bufToBase64(iv),
    ct: bufToBase64(ct),
  };
}
